import React from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Card, CardContent } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { AlertTriangle, CheckCircle2, User, MessageSquare, History, Send } from "lucide-react";

/**
 * IncidentDetailModal — Diálogo de detalle/gestión de una incidencia.
 *
 * Extraído 1:1 desde `pages/IncidentsPage.js` (el modal más pesado de la página).
 * Muestra cabecera (estado, prioridad, cliente, servicio), título/descripción,
 * cambio de estado y asignación de técnico (según permisos), resumen de resolución
 * si está resuelta, y pestañas de comentarios (con alta) e historial de actividad.
 *
 * @param {boolean}     props.detailModalOpen       Apertura del diálogo.
 * @param {Function}    props.setDetailModalOpen     Setter del flag de apertura.
 * @param {Object|null} props.selectedIncident       Incidencia seleccionada (null = sin datos).
 * @param {Object}      props.statusColors           Mapa estado→clases de color del badge.
 * @param {Object}      props.statusLabels           Mapa estado→etiqueta legible.
 * @param {Object}      props.priorityColors         Mapa prioridad→clases de color del badge.
 * @param {Object}      props.priorityLabels         Mapa prioridad→etiqueta legible.
 * @param {boolean}     props.canEdit                Permiso para cambiar estado/asignar.
 * @param {Function}    props.handleStatusChange     (newStatus) => void — cambia el estado.
 * @param {Function}    props.handleAssignTechnician (assigneeId|null) => void — asigna técnico.
 * @param {Array}       props.supportUsers           Usuarios asignables (técnicos/soporte).
 * @param {Function}    props.formatDate             (dateStr) => string — formatea fechas.
 * @param {string}      props.newComment             Texto del comentario en edición.
 * @param {Function}    props.setNewComment          Setter del comentario.
 * @param {Function}    props.handleAddComment       Añade el comentario a la incidencia.
 */
export const IncidentDetailModal = ({
  detailModalOpen,
  setDetailModalOpen,
  selectedIncident,
  statusColors,
  statusLabels,
  priorityColors,
  priorityLabels,
  canEdit,
  handleStatusChange,
  handleAssignTechnician,
  supportUsers,
  formatDate,
  newComment,
  setNewComment,
  handleAddComment,
}) => {
  return (
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              {selectedIncident?.incident_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-6 mt-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Estado</p>
                  <Badge className={`${statusColors[selectedIncident.status]} text-white mt-1`}>
                    {statusLabels[selectedIncident.status]}
                  </Badge>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Prioridad</p>
                  <Badge className={`${priorityColors[selectedIncident.priority]} text-white mt-1`}>
                    {priorityLabels[selectedIncident.priority]}
                  </Badge>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Cliente</p>
                  <p className="text-white font-medium text-sm">{selectedIncident.customer_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Servicio</p>
                  <p className="text-white font-medium text-sm">{selectedIncident.service_name || "General"}</p>
                </div>
              </div>

              {/* Title & Description */}
              <div className="p-4 rounded-lg bg-[#1B1B4B]/30 border border-[#1E1E5F]">
                <h3 className="text-white font-semibold text-lg mb-2">{selectedIncident.title}</h3>
                <p className="text-slate-300">{selectedIncident.description}</p>
              </div>

              {/* Status Change (only for allowed roles) */}
              {canEdit && selectedIncident.status !== "RESUELTO" && (
                <div className="p-4 rounded-lg bg-[#1B1B4B]/30 border border-[#1E1E5F]">
                  <Label className="text-slate-300">Cambiar Estado</Label>
                  <Select
                    value={selectedIncident.status}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="NUEVA" className="text-red-400">Nueva</SelectItem>
                      <SelectItem value="EN_TRATAMIENTO" className="text-blue-400">En Tratamiento</SelectItem>
                      <SelectItem value="PENDIENTE_PROVEEDOR" className="text-purple-400">Pendiente Proveedor</SelectItem>
                      <SelectItem value="PENDIENTE_CLIENTE" className="text-yellow-400">Pendiente Cliente</SelectItem>
                      <SelectItem value="RESUELTO" className="text-green-400">Resuelto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Technician Assignment — same behaviour as Tickets Internos */}
              <div className="p-4 rounded-lg bg-[#1B1B4B]/30 border border-[#1E1E5F]">
                <Label className="text-slate-300 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Técnico asignado
                </Label>
                {canEdit && selectedIncident.status !== "RESUELTO" ? (
                  <Select
                    value={selectedIncident.assigned_to_id || "none"}
                    onValueChange={(v) => handleAssignTechnician(v === "none" ? null : v)}
                  >
                    <SelectTrigger
                      data-testid="incident-assign-technician-select"
                      className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    >
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="none" className="text-slate-400">Sin asignar</SelectItem>
                      {supportUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id} className="text-white">
                          {u.name || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-white mt-1">
                    {selectedIncident.assigned_to_name || "Sin asignar"}
                  </p>
                )}
              </div>

              {/* Resolution Summary (if resolved) */}
              {selectedIncident.status === "RESUELTO" && selectedIncident.resolution_summary && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Resumen de la Solución
                  </h4>
                  <p className="text-slate-300">{selectedIncident.resolution_summary}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    Resuelto por {selectedIncident.resolved_by_name} el {formatDate(selectedIncident.resolved_at)}
                  </p>
                </div>
              )}

              {/* Tabs for Comments and Activity Log */}
              <Tabs defaultValue="comments" className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-[#1B1B4B]">
                  <TabsTrigger value="comments" className="data-[state=active]:bg-[#03BEE4]">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Comentarios ({selectedIncident.comments?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-[#03BEE4]">
                    <History className="h-4 w-4 mr-2" />
                    Historial
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="comments" className="mt-4">
                  <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                    <CardContent className="pt-4">
                      {/* Add Comment */}
                      <div className="flex gap-2 mb-4">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Añadir comentario técnico..."
                          className="bg-[#0A0A35] border-[#1E1E5F] text-white h-20 flex-1"
                        />
                        <Button
                          onClick={handleAddComment}
                          disabled={!newComment.trim()}
                          className="bg-[#42E695] text-[#04042C] self-end"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <ScrollArea className="h-60">
                        <div className="space-y-3">
                          {selectedIncident.comments?.length === 0 ? (
                            <p className="text-slate-400 text-center py-4">No hay comentarios</p>
                          ) : (
                            selectedIncident.comments?.map((comment) => (
                              <div key={comment.id} className="p-3 rounded-lg bg-[#0A0A35] border border-[#1E1E5F]">
                                <div className="flex justify-between items-start mb-1">
                                  <span className="text-[#03BEE4] text-sm font-medium">{comment.user_name}</span>
                                  <span className="text-xs text-slate-400">{formatDate(comment.timestamp)}</span>
                                </div>
                                <p className="text-slate-300 text-sm">{comment.content}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                    <CardContent className="pt-4">
                      <ScrollArea className="h-60">
                        <div className="space-y-2">
                          {selectedIncident.activity_log?.map((log) => (
                            <div key={log.id} className="flex gap-3 p-2 rounded bg-[#0A0A35]">
                              <div className="w-2 h-2 rounded-full bg-[#03BEE4] mt-2"></div>
                              <div className="flex-1">
                                <p className="text-white text-sm">{log.action}</p>
                                {log.details && <p className="text-slate-400 text-xs">{log.details}</p>}
                                <p className="text-xs text-slate-500 mt-1">
                                  {log.user_name} • {formatDate(log.timestamp)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
